import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { CampaignModalShell } from '#/entities/campaign'
import { toIsoDate } from '#/shared/lib/timezone'
import { DatePicker } from '#/shared/ui/date-picker'
import { Input } from '#/shared/ui/input'
import { Select } from '#/shared/ui/select'

import {
  CAMPAIGN_TASK_TYPES,
  CAMPAIGN_TASK_TYPE_ORDER,
} from '../model/use-campaign-people-actions'
import type {
  CampaignTaskType,
  CampaignTasksInput,
} from '../model/use-campaign-people-actions'

interface CampaignTasksDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  count: number
  busy: boolean
  onSubmit: (input: CampaignTasksInput) => void
}

/**
 * "Create tasks": one task per ticked person, hung on their quote. What it
 * says, what kind it is, and (optionally) when it is due — the service reports
 * the people with no quote to hang it on as skipped.
 */
export function CampaignTasksDialog({
  open,
  onOpenChange,
  count,
  busy,
  onSubmit,
}: CampaignTasksDialogProps) {
  const { t } = useTranslation()
  const [title, setTitle] = useState('')
  const [type, setType] = useState<CampaignTaskType>(CAMPAIGN_TASK_TYPES.call)
  const [date, setDate] = useState<Date | undefined>(undefined)
  const typeOptions = CAMPAIGN_TASK_TYPE_ORDER.map((value) => ({
    value,
    label: t(`campaigns.modal.tasks.types.${value}`),
  }))
  return (
    <CampaignModalShell
      open={open}
      onOpenChange={onOpenChange}
      size="sm"
      title={t('campaigns.modal.tasks.title', { count })}
      description={t('campaigns.modal.tasks.description')}
      submitLabel={t('campaigns.modal.tasks.submit', { count })}
      submitDisabled={busy || title.trim() === ''}
      onSubmit={() =>
        onSubmit({
          title,
          type,
          date: date == null ? null : toIsoDate(date),
        })
      }
    >
      <Input
        label={t('campaigns.modal.tasks.titleLabel')}
        value={title}
        placeholder={t('campaigns.modal.tasks.titlePlaceholder')}
        onChange={(event) => setTitle(event.target.value)}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <Select
          portal
          label={t('campaigns.modal.tasks.typeLabel')}
          options={typeOptions}
          value={type}
          onValueChange={(value) => {
            const next = CAMPAIGN_TASK_TYPE_ORDER.find((item) => item === value)
            if (next != null) setType(next)
          }}
        />
        <DatePicker
          label={t('campaigns.modal.tasks.dateLabel')}
          placeholder={t('campaigns.modal.tasks.datePlaceholder')}
          value={date}
          onValueChange={setDate}
          instantApply
        />
      </div>
    </CampaignModalShell>
  )
}
